import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, HostListener, inject, input, signal, type WritableSignal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import { DiagnosticRating, INSTAGRAM_VISIBILITY_RATING } from '@malou-io/package-utils';

import type { Diagnostic } from ':shared/models';

import { PartnerThemeContext } from ':core/context/partner-theme.context';
import { RatingBlocComponent } from ':modules/rating-bloc/rating-bloc.component';
import { RestaurantInformationComponent } from ':modules/restaurant-information/restaurant-information.component';
import { PHONE_SCREEN_BREAKPOINT } from ':shared/constants';
import { SvgIcon } from ':shared/enums/svg-icon.enum';
import {
    Illustration,
    IllustrationPathResolverPipe,
    ImagePathResolverPipe,
    LogoPathResolverPipe,
    PluralTranslatePipe,
} from ':shared/pipes';

@Component({
    selector: 'app-diagnostic-score',
    imports: [
        NgClass,
        NgTemplateOutlet,
        RatingBlocComponent,
        MatIconModule,
        TranslateModule,
        IllustrationPathResolverPipe,
        ImagePathResolverPipe,
        PluralTranslatePipe,
        RestaurantInformationComponent,
        LogoPathResolverPipe,
    ],
    templateUrl: './diagnostic-score.component.html',
    styleUrl: './diagnostic-score.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticScoreComponent {
    readonly isPreview = input<boolean>(true);
    readonly diagnostic = input.required<Diagnostic>();

    private readonly _partnerThemeContext = inject(PartnerThemeContext);

    readonly DiagnosticRating = DiagnosticRating;
    readonly Illustration = Illustration;
    readonly SvgIcon = SvgIcon;

    readonly isPhoneScreen: WritableSignal<boolean> = signal(false);
    readonly partnerTheme = this._partnerThemeContext.activeTheme;

    readonly score = computed(() => this.diagnostic().getTotalScore());
    readonly scoreGaugeImage = computed(() => this._getScoreGaugeImageByScore(this.score()));
    readonly scoreRating = computed(() => this.diagnostic().getTotalScoreRating());

    readonly inconsistencies = computed(() => this.diagnostic().inconsistencies);
    readonly inconsistencyCount = computed(() => this.inconsistencies()?.inconsistencyCount ?? 0);
    readonly inconsistenciesRating = computed(() => this.diagnostic().getInconsistencyRating());

    readonly betterLocalCompetitors = computed(() => this.diagnostic().getBetterLocalCompetitorCount());
    readonly googleRating = computed(() => this.diagnostic().getGoogleRating());

    readonly reviewCount = computed(() => this.diagnostic().reviewCount);
    readonly reviewCountDiff = computed(() => this.diagnostic().getReviewCountDiff());
    readonly reviewCountRating = computed(() => this.diagnostic().getReviewCountRating());

    readonly hasInstagram = computed(() => !!this.diagnostic().instagramPage?.name);
    readonly instagramVisibilityRating = computed(() => {
        const instagramScore = this.diagnostic().getInstagramScore();
        if (instagramScore < INSTAGRAM_VISIBILITY_RATING.MINIMUM_AVERAGE_THRESHOLD) {
            return DiagnosticRating.BAD;
        } else if (instagramScore < INSTAGRAM_VISIBILITY_RATING.MINIMUM_GOOD_THRESHOLD) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.GOOD;
    });

    @HostListener('window:resize')
    onResize() {
        this.isPhoneScreen.set(window.innerWidth < PHONE_SCREEN_BREAKPOINT);
    }

    private _getScoreGaugeImageByScore(score: number): string {
        if (score < 10) {
            return 'gauge-10';
        }
        if (score > 90) {
            return 'gauge-90';
        }
        const images = ['gauge-10', 'gauge-20', 'gauge-30', 'gauge-40', 'gauge-50', 'gauge-60', 'gauge-70', 'gauge-80', 'gauge-90'];
        const index = Math.floor(score / 10) - 1;
        return images[index];
    }
}
